import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, OnInit } from "@angular/core";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material";
import {
  EnrichrBackgroundType,
  EnrichrGOBackground,
  EnrichrGSEAResults,
  EnrichrPathwaysBackground,
  availableEnrichrBackgrounds,
} from "./enrichment-analysis.service.types";
import { Observable, Subject, of } from "rxjs";
import { finalize, first, map, startWith, takeUntil } from "rxjs/operators";

export enum EA_API {
  GENE_ONTOLOGY = "geneOntology",
}

type CachedBackgrounds = Record<EnrichrGOBackground | EnrichrPathwaysBackground, Map<string, string[]>>;

@Injectable({
  providedIn: "root",
})
export class EnrichmentAnalysisService {
  // keep track of if a request is being made for each api, for rate limiting
  static apiAvailability = {
    [EA_API.GENE_ONTOLOGY]: true,
  };

  public static AVAILABLE_BACKGROUNDS = availableEnrichrBackgrounds;
  /** key is background. value is a map of term to genes list */
  private cachedBackgrounds: CachedBackgrounds = [
    ...Object.keys(EnrichrGOBackground),
    ...Object.keys(EnrichrPathwaysBackground),
  ].reduce(
    (acc, cur) => ({ ...acc, [cur]: new Map() }),
    {} as CachedBackgrounds
  );

  /** key is gene names concatted with a + and the background dataset name */
  private cachedResults: { [key: string]: EnrichrGSEAResults } = {};

  private snackbarConfig: MatSnackBarConfig<any> = {
    horizontalPosition: "right",
    verticalPosition: "top",
    duration: 3000,
  };

  /** Given a list of genes and background Type, get cached results, if any */
  getCachedResult(
    genes: string[],
    backgroundType: string,
    regulation: "up" | "down" | "none"
  ): EnrichrGSEAResults {
    const sortedGenesToCache = [...genes].sort();
    const cacheKey = [...sortedGenesToCache, backgroundType, regulation].join(
      "+"
    );
    return this.cachedResults[cacheKey];
  }

  private async loadAllBackgroundDatasetMappings(
    backgroundType: EnrichrBackgroundType,
    cancel: Subject<void> = new Subject(),
    onCanceled: () => void = () => {}
  ): Promise<Map<string, string[]>> {
    // determine which background type to use
    const allBackground =
      backgroundType === EnrichrBackgroundType.GO
        ? EnrichrGOBackground.ALL
        : EnrichrPathwaysBackground.ALL;

    // get all associated backgrounds of the given type, except for the "all" background
    const backgrounds = availableEnrichrBackgrounds.filter(
      (b) => b.type === backgroundType && b.value !== allBackground
    );

    // load all mappings in parallel
    const allMappings: Map<string, string[]>[] = await Promise.all(
      backgrounds.map((b) =>
        this.loadBackgroundDatasetMapping(b.value, cancel, onCanceled)
      )
    );

    // combine all mappings into a single flat map
    const flat = new Map<string, string[]>();
    allMappings.forEach((mapping) => {
      mapping.forEach((value, key) => {
        flat.set(key, value);
      });
    });
    return flat;
  }

  async loadBackgroundDatasetMapping(
    dataset: EnrichrPathwaysBackground | EnrichrGOBackground,
    cancel: Subject<void> = new Subject(),
    onCanceled: () => void = () => {}
  ): Promise<Map<string, string[]>> {
    if (dataset in this.cachedBackgrounds) {
      return this.cachedBackgrounds[dataset];
    }

    if (dataset === EnrichrGOBackground.ALL) {
      return this.loadAllBackgroundDatasetMappings(
        EnrichrBackgroundType.GO,
        cancel,
        onCanceled
      );
    } else if (dataset === EnrichrPathwaysBackground.ALL) {
      return this.loadAllBackgroundDatasetMappings(
        EnrichrBackgroundType.PATHWAY,
        cancel,
        onCanceled
      );
    }

    const url = `https://maayanlab.cloud/Enrichr/geneSetLibrary?mode=text&libraryName=${dataset}`;
    return this.http
      .get(url, { responseType: "text" })
      .pipe(
        takeUntil(cancel),
        finalize(() => {
          EnrichmentAnalysisService.apiAvailability[EA_API.GENE_ONTOLOGY] =
            true;
          onCanceled();
        })
      )
      .toPromise()
      .then((tsvData: string) => {
        // Split the TSV data into rows
        const rows = tsvData.split("\n");
        const termToGenesMapping = new Map<string, string[]>();
        rows.map((row) => {
          const columns = row.split("\t");
          termToGenesMapping.set(columns[0], columns.slice(1));
        });
        this.cachedBackgrounds[dataset] = termToGenesMapping;
        return termToGenesMapping;
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        throw error;
      });
  }

  async getGenesByTerm(
    dataset: EnrichrGOBackground | EnrichrPathwaysBackground,
    term: string,
    cancel: Subject<void> = new Subject<void>(),
    onCanceled: () => void = () => {}
  ): Promise<string[]> {
    const termToGenesMapping = await this.loadBackgroundDatasetMapping(
      dataset,
      cancel,
      onCanceled
    );
    const genes = termToGenesMapping.get(term);
    if (!genes) {
      throw new Error(`Term ${term} not found in dataset ${dataset}`);
    }
    return genes;
  }

  /**
   * @description Register a list of genes in the Enrichr database.
   * @param genes list of genes
   * @returns {Observable<{userListId: number, shortId: string}>} the user list id and short id of the newly created list
   *
   */
  private createEnrichrGeneList(genes: string[]): Observable<{
    userListId: number;
    shortId: string;
  }> {
    const ENDPOINT = "https://maayanlab.cloud/Enrichr/addList";
    const description = "Example gene list";
    const formData = new FormData();
    formData.append("list", genes.join("\n"));
    formData.append("description", description);

    // Define headers to specify content type
    const headers = new HttpHeaders();
    headers.append("Content-Type", "multipart/form-data");

    return this.http.post<any>(ENDPOINT, formData, { headers: headers });
  }

  /**
   * Special case for when the background type is "All_GO_Terms" or "All_Pathways". This calls `runEnrichrGSEA` for each available background of the requested type
   */
  private async runEnrichrGSEAAll(
    genes: string[],
    backgroundType: EnrichrBackgroundType,
    regulation: "up" | "down"
  ): Promise<Observable<EnrichrGSEAResults>> {
    const allBackground =
      backgroundType === EnrichrBackgroundType.GO
        ? EnrichrGOBackground.ALL
        : EnrichrPathwaysBackground.ALL;

    const promises = availableEnrichrBackgrounds
      .filter((b) => b.type === backgroundType && b.value !== allBackground)
      .map(async (b) =>
        this.runEnrichrGSEA(genes, b.value, regulation).then((obs) =>
          obs.toPromise()
        )
      );

    const allResults = await Promise.all(promises);
    return of(allResults.reduce((acc, val) => acc.concat(val), []));
  }

  /**
   *
   * @param genes list of genes
   * @param backgroundType Background Dataset type (e.g. "KEGG_2019_Human" or "GO_Biological_Process_2018")
   * @param regulation "up" or "down" for upregulated or downregulated genes (not used in calculation, just for caching lookup purposes)
   * @returns
   */
  async runEnrichrGSEA(
    genes: string[],
    backgroundType: (typeof EnrichmentAnalysisService)["AVAILABLE_BACKGROUNDS"][number]["value"],
    regulation: "up" | "down" = "up"
  ): Promise<Observable<EnrichrGSEAResults>> {
    // create a key for the cache
    const sortedGenesToCache = [...genes].sort();
    const cacheKey = [...sortedGenesToCache, backgroundType, regulation].join(
      "+"
    );

    // see if we have the results cached
    if (cacheKey in this.cachedResults) {
      return of(this.cachedResults[cacheKey]);
    }

    if (backgroundType === EnrichrGOBackground.ALL) {
      return this.runEnrichrGSEAAll(
        genes,
        EnrichrBackgroundType.GO,
        regulation
      );
    } else if (backgroundType === EnrichrPathwaysBackground.ALL) {
      return this.runEnrichrGSEAAll(
        genes,
        EnrichrBackgroundType.PATHWAY,
        regulation
      );
    }

    // otherwise create a new list and run the analysis
    const { userListId, shortId } = await this.createEnrichrGeneList(
      genes
    ).toPromise();

    const ENDPOINT = "https://maayanlab.cloud/Enrichr/enrich";
    const urlParams = {
      userListId: userListId.toString(),
      backgroundType: backgroundType.toString(),
    };

    const full_url = ENDPOINT + "?" + new URLSearchParams(urlParams).toString();

    return this.http.get(full_url).pipe(
      map((res: any) => {
        const data = res[backgroundType];
        const results: EnrichrGSEAResults = data.map((d: Array<any>) => {
          return {
            index: d[0],
            term: d[1],
            pValue: d[2],
            oddsRatio: d[3],
            combinedScore: d[4],
            overlappingGenes: d[5],
            adjPValue: d[6],
          };
        });

        // cache the results
        this.cachedResults[cacheKey] = results;

        return results;
      })
    );
  }

  constructor(private http: HttpClient, private snackbar: MatSnackBar) {}
}
